// The account's Orpheus consent, shared by every surface that asks (v3 `orpheusConsentStore.ts`,
// ORP-24540): the page's gate, the settings pane, the feature-chat panels and the Alerts page all
// read and write the same answer, so turning Orpheus on in one is on in all of them.
//
// The chat endpoints enforce consent on the server whatever this says. This store only decides
// which screen to draw, and it never draws the closed one on a guess: a status it could not read
// lets the user through, and the first refused send flips it to off.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { OrpheusConsentApi } from '../api/OrpheusConsentApi'
import type {
  OrpheusConsentStatusDto,
  OrpheusConsentUser,
  OrpheusConsentVariant,
} from '../models/orpheusConsent.types'
import {
  contactableAdmins,
  readConsentProbe,
  toOrpheusConsent,
  type OrpheusConsentProbeResult,
} from '../service/orpheusConsent'

import { ApiError } from '@/shared/http'

export type ConsentLoadError = 'forbidden' | 'error'

export const useOrpheusConsentStore = defineStore('orpheusConsentStore', () => {
  const api = new OrpheusConsentApi()

  const given = ref(false)
  const grantedAt = ref<string | null>(null)
  const grantedBy = ref<OrpheusConsentUser | null>(null)
  const accountAdmins = ref<OrpheusConsentUser[]>([])
  const loading = ref(false)
  const loaded = ref(false)
  const loadError = ref<ConsentLoadError | null>(null)
  /**
   * Neither the status read nor the member probe could tell whether the account consented. The
   * chat/ask endpoints still enforce consent, so the UI lets the user through and flips to the
   * closed screen when a send comes back ORPHEUS_CONSENT_REQUIRED.
   */
  const statusUnknown = ref(false)
  /** The account the answer above belongs to; another account's answer is never reused. */
  const accountId = ref<number | string | null>(null)

  /** Known to be off — the only state that swaps the chat for the consent/closed screens. */
  const isOff = computed(() => loaded.value && !statusUnknown.value && !given.value)

  function applyStatus(dto: OrpheusConsentStatusDto): void {
    const consent = toOrpheusConsent(dto)
    given.value = consent.given
    grantedAt.value = consent.grantedAt
    grantedBy.value = consent.grantedBy
    accountAdmins.value = contactableAdmins(dto.account_admins)
    loaded.value = true
    loadError.value = null
    statusUnknown.value = false
  }

  /** A chat/ask call came back 403 ORPHEUS_CONSENT_REQUIRED: the account is off, for sure. */
  function markConsentRequired(admins: OrpheusConsentUser[]): void {
    given.value = false
    grantedAt.value = null
    grantedBy.value = null
    accountAdmins.value = admins
    loaded.value = true
    loadError.value = null
    statusUnknown.value = false
  }

  function markGiven(): void {
    given.value = true
    loaded.value = true
    loadError.value = null
    statusUnknown.value = false
  }

  /**
   * Read the account's status. Silent on failure: a member (whom the status read refuses) is
   * answered by the chat gate itself, and anything still unknown settles as "let through" rather
   * than leaving the page spinning.
   */
  async function fetchConsent(
    account: number | string | undefined,
    workspace: number | string | undefined,
    force = false,
  ): Promise<void> {
    if (account === undefined) {
      // Nothing to ask about yet: let the page through rather than spin; the server still gates.
      reset()
      statusUnknown.value = true
      loaded.value = true
      return
    }
    if (String(accountId.value) !== String(account)) {
      reset()
      accountId.value = account
    } else if (loading.value || (loaded.value && !statusUnknown.value && !force)) {
      return
    }

    loading.value = true
    try {
      const dto = await api.fetch(account)
      if (String(accountId.value) === String(account)) applyStatus(dto)
    } catch (cause) {
      if (String(accountId.value) !== String(account)) return
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (error.kind === 'canceled') return
      const probe =
        error.status === 403 && workspace !== undefined ? await probeConsent(workspace) : null
      if (String(accountId.value) !== String(account)) return
      if (probe?.off) markConsentRequired(probe.accountAdmins)
      else if (probe) markGiven()
      else {
        loadError.value = error.kind === 'forbidden' ? 'forbidden' : 'error'
        statusUnknown.value = true
        loaded.value = true
      }
    } finally {
      if (String(accountId.value) === String(account)) loading.value = false
    }
  }

  async function probeConsent(workspace: number | string): Promise<OrpheusConsentProbeResult> {
    try {
      await api.probe(workspace)
      return { off: false }
    } catch (cause) {
      return readConsentProbe(cause)
    }
  }

  /** Rejects with the API's error; the caller words it. */
  async function grant(
    account: number | string,
    variant: OrpheusConsentVariant = 'orphex_credits',
  ): Promise<void> {
    const dto = await api.grant(account, variant)
    if (String(accountId.value) === String(account)) applyStatus(dto)
  }

  async function revoke(account: number | string): Promise<void> {
    const dto = await api.revoke(account)
    if (String(accountId.value) === String(account)) applyStatus(dto)
  }

  function reset(): void {
    given.value = false
    grantedAt.value = null
    grantedBy.value = null
    accountAdmins.value = []
    loading.value = false
    loaded.value = false
    loadError.value = null
    statusUnknown.value = false
    accountId.value = null
  }

  return {
    given,
    grantedAt,
    grantedBy,
    accountAdmins,
    loading,
    loaded,
    loadError,
    statusUnknown,
    accountId,
    isOff,
    applyStatus,
    markConsentRequired,
    fetchConsent,
    grant,
    revoke,
    reset,
  }
})

export type OrpheusConsentStore = ReturnType<typeof useOrpheusConsentStore>
